<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import {
    mdiAccountCircleOutline,
    mdiChevronDown,
    mdiHeartPulse,
    mdiLogoutVariant,
    mdiPalette,
    mdiTranslate,
    mdiMenu
} from '@mdi/js';
import { healthApi } from '@/api/health';
import { SupportedLocales, LocaleLabels, type Locale, translateLabel } from '@/i18n';
import { useAuthStore } from '@/stores/auth';
import { useLocaleStore } from '@/stores/locale';
import { useModulesStore } from '@/stores/modules';
import { brandThemes, ThemeKeys, useThemeStore, type ThemeMode } from '@/stores/theme';
import MdiIcon from './MdiIcon.vue';

defineProps<{ navigationOpen?: boolean }>();
defineEmits<{ (event: 'toggle-navigation'): void }>();

const route = useRoute();
const auth = useAuthStore();
const theme = useThemeStore();
const locale = useLocaleStore();
const modules = useModulesStore();
const { t } = useI18n();
const title = computed(() => {
    const raw = (route.meta.title as string | undefined) ?? '';
    const moduleId = route.meta.moduleId as string | undefined;
    const fallback = raw.endsWith('.title')
        ? modules.manifests.find(manifest => manifest.id === moduleId)?.displayName
        : undefined;
    return translateLabel(raw, fallback);
});

const healthStatus = ref<'unknown' | 'healthy' | 'degraded' | 'down'>('unknown');
const healthDetail = ref('');

async function refreshHealth() {
    try {
        const r = await healthApi.report();
        healthStatus.value = r.status === 'healthy' ? 'healthy' : 'degraded';
        // Backend returns { status, modules: { name: { status, timestamp } } }
        // or { status, checks: [{ name, status }] } — handle both
        const checks = (r as any).checks ?? [];
        const modules = (r as any).modules ?? {};
        const failed: string[] = [];

        if (checks.length > 0) {
            // Old format: checks array
            for (const c of checks) {
                if (c.status !== 'healthy') failed.push(c.name);
            }
        } else if (Object.keys(modules).length > 0) {
            // New format: modules object
            for (const [name, info] of Object.entries(modules)) {
                if ((info as any).status !== 'healthy') failed.push(name);
            }
        }

        healthDetail.value = failed.length
            ? t('topbar.health.degradedFmt', { services: failed.join(', ') })
            : t('topbar.health.allOk');
    } catch (e) {
        healthStatus.value = 'down';
        healthDetail.value = e instanceof Error ? e.message : 'health check failed';
    }
}

let healthTimer: number | undefined;
onMounted(() => {
    void refreshHealth();
    healthTimer = window.setInterval(() => void refreshHealth(), 30_000);
});
onUnmounted(() => {
    if (healthTimer !== undefined) window.clearInterval(healthTimer);
});

const userMenuOpen = ref(false);
const langMenuOpen = ref(false);
const themeMenuOpen = ref(false);

function toggleUserMenu() {
    userMenuOpen.value = !userMenuOpen.value;
    if (userMenuOpen.value) {
        langMenuOpen.value = false;
        themeMenuOpen.value = false;
    }
}

function closeUserMenu() {
    userMenuOpen.value = false;
}

async function handleLogout() {
    closeUserMenu();
    await auth.logout();
}

function toggleLangMenu() {
    langMenuOpen.value = !langMenuOpen.value;
    if (langMenuOpen.value) {
        userMenuOpen.value = false;
        themeMenuOpen.value = false;
    }
}

function pickLocale(lang: Locale) {
    locale.set(lang);
    langMenuOpen.value = false;
}

function toggleThemeMenu() {
    themeMenuOpen.value = !themeMenuOpen.value;
    if (themeMenuOpen.value) {
        userMenuOpen.value = false;
        langMenuOpen.value = false;
    }
}

function pickTheme(mode: ThemeMode) {
    theme.set(mode);
    themeMenuOpen.value = false;
}
</script>

<template>
    <header class="top-bar">
        <div class="top-bar__title">
            <button class="navigation-toggle" type="button" aria-label="Меню" aria-controls="shell-navigation"
                :aria-expanded="navigationOpen" @click="$emit('toggle-navigation')">
                <MdiIcon :path="mdiMenu" :size="24" />
            </button>
            <h1>{{ title }}</h1>
        </div>

        <div class="top-bar__actions">
            <span class="health" :class="`health--${healthStatus}`" :title="healthDetail">
                <MdiIcon :path="mdiHeartPulse" :size="16" />
                <span class="health__label">{{
                    healthStatus === 'healthy'
                        ? t('topbar.health.ok')
                        : healthStatus === 'degraded'
                          ? t('topbar.health.degraded')
                          : healthStatus === 'down'
                            ? t('topbar.health.down')
                            : t('topbar.health.unknown')
                }}</span>
            </span>

            <div class="theme-menu">
                <button
                    class="theme-toggle"
                    type="button"
                    @click="toggleThemeMenu"
                    :title="t('topbar.theme')"
                    :aria-expanded="themeMenuOpen"
                >
                    <span class="theme-toggle__swatch" aria-hidden="true" />
                    <span class="theme-toggle__name">{{ t(`themes.${theme.mode}`) }}</span>
                    <MdiIcon :path="mdiChevronDown" :size="16" />
                </button>
                <div v-if="themeMenuOpen" class="menu-dropdown theme-menu__dropdown" role="menu">
                    <button
                        v-for="key in ThemeKeys"
                        :key="key"
                        class="menu-item"
                        :class="{ 'menu-item--active': theme.mode === key }"
                        type="button"
                        @click="pickTheme(key)"
                    >
                        <span
                            class="theme-menu__swatch"
                            :style="{ background: `linear-gradient(135deg, ${brandThemes[key].config.accent.gradientStart} 0%, ${brandThemes[key].config.accent.gradientEnd} 100%)` }"
                            aria-hidden="true"
                        />
                        <MdiIcon :path="mdiPalette" :size="16" />
                        <span>{{ t(`themes.${key}`) }}</span>
                    </button>
                </div>
            </div>

            <div class="lang-menu">
                <button
                    class="lang-toggle"
                    type="button"
                    @click="toggleLangMenu"
                    :title="t('topbar.language')"
                    :aria-expanded="langMenuOpen"
                >
                    <MdiIcon :path="mdiTranslate" :size="16" />
                    <span class="lang-toggle__code">{{ locale.current.toUpperCase() }}</span>
                </button>
                <div v-if="langMenuOpen" class="menu-dropdown" role="menu">
                    <button
                        v-for="lang in SupportedLocales"
                        :key="lang"
                        class="menu-item"
                        :class="{ 'menu-item--active': locale.current === lang }"
                        type="button"
                        @click="pickLocale(lang)"
                    >
                        <span class="lang-toggle__code">{{ lang.toUpperCase() }}</span>
                        <span>{{ LocaleLabels[lang] }}</span>
                    </button>
                </div>
            </div>

            <div class="user-menu" @blur.capture="closeUserMenu" tabindex="-1">
                <button
                    class="user-menu__trigger"
                    @click="toggleUserMenu"
                    type="button"
                    :aria-expanded="userMenuOpen"
                >
                    <MdiIcon :path="mdiAccountCircleOutline" :size="20" />
                    <span class="user-menu__name">{{
                        auth.user?.displayName || auth.user?.email || t('topbar.guest')
                    }}</span>
                    <span class="chip">{{ auth.role ?? '—' }}</span>
                </button>
                <div v-if="userMenuOpen" class="menu-dropdown user-menu__dropdown" role="menu">
                    <div class="user-menu__email">{{ auth.user?.email }}</div>
                    <button class="menu-item" type="button" @click="handleLogout">
                        <MdiIcon :path="mdiLogoutVariant" :size="18" />
                        <span>{{ t('topbar.logout') }}</span>
                    </button>
                </div>
            </div>
        </div>
    </header>
</template>

<style scoped>
.top-bar {
    grid-area: top;
    z-index: 1;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    padding: 0 1.5rem;
    background: var(--shell-nav);
    border-bottom: 1px solid var(--shell-divider);
    min-height: 56px;
}
.top-bar__title h1 {
    margin: 0;
    font-size: 16px;
    font-weight: 600;
    line-height: 1.2;
}
.top-bar__title { display: flex; align-items: center; gap: 8px; min-width: 0; }
.navigation-toggle { display: none; border: 0; background: transparent; color: var(--ink); }
.top-bar__actions {
    display: flex;
    align-items: center;
    gap: 0.75rem;
}
.health {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
    color: var(--ink-mute);
}
.health--healthy { color: var(--state-ok); }
.health--degraded { color: var(--state-warn); }
.health--down { color: var(--state-err); }

.theme-menu,
.lang-menu,
.user-menu {
    position: relative;
}
.theme-toggle,
.lang-toggle {
    display: flex;
    align-items: center;
    gap: 6px;
    border: 1px solid var(--shell-divider);
    background: var(--surface);
    border-radius: 6px;
    padding: 6px 10px;
    font-size: 12px;
    color: var(--ink-soft);
    transition: background var(--transition-fast);
}
.theme-toggle:hover,
.lang-toggle:hover {
    background: var(--hover-soft);
}
.theme-toggle__swatch {
    width: 16px;
    height: 16px;
    border-radius: 999px;
    background: var(--accent-gradient);
    flex-shrink: 0;
}
.theme-menu__swatch {
    width: 18px;
    height: 18px;
    border-radius: 999px;
    flex-shrink: 0;
}
.lang-toggle__code {
    font-weight: 600;
    font-size: 11px;
}
.user-menu__trigger {
    display: flex;
    align-items: center;
    gap: 8px;
    border: 1px solid var(--shell-divider);
    background: var(--surface);
    border-radius: 6px;
    padding: 6px 10px;
    font-size: 12px;
    color: var(--ink-soft);
    transition: background var(--transition-fast);
}
.user-menu__trigger:hover {
    background: var(--hover-soft);
}
.user-menu__name {
    max-width: 140px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}
.chip {
    display: inline-flex;
    align-items: center;
    padding: 2px 8px;
    border-radius: 999px;
    background: var(--accent-tint);
    color: var(--accent);
    font-size: 10px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.05em;
}
.user-menu__dropdown {
    min-width: 200px;
}
.user-menu__email {
    padding: 8px 12px;
    border-bottom: 1px solid var(--shell-divider);
    font-size: 12px;
    color: var(--ink-mute);
    word-break: break-all;
}

@media (max-width: 900px) {
    .navigation-toggle { display: grid; place-items: center; min-width: 44px; min-height: 44px; }
    .top-bar {
        padding: 0 8px;
        gap: 4px;
    }
    .top-bar__title h1 { font-size: 13px; overflow-wrap: anywhere; }
    .top-bar__actions { gap: 4px; }
    .theme-toggle, .lang-toggle, .user-menu__trigger { min-height: 44px; padding: 6px; }
    .chip, .health { display: none; }
    .health__label,
    .theme-toggle__name,
    .user-menu__name {
        display: none;
    }
}
</style>
